"use client";

import { Calendar, User, ArrowLeft, ArrowRight } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="h-12 w-3/4 rounded-lg bg-muted/40 animate-pulse" />
        <div className="h-6 w-1/2 rounded-md bg-muted/40 animate-pulse" />
        <div className="h-96 w-full rounded-2xl bg-muted/40 animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 w-full rounded bg-muted/40 animate-pulse" />
          <div className="h-4 w-11/12 rounded bg-muted/40 animate-pulse" />
          <div className="h-4 w-10/12 rounded bg-muted/40 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-muted-foreground text-lg">Unable to load this update at the moment.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center py-16">
        <p className="text-muted-foreground text-lg">No update found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorInitial = post.author?.name ? post.author.name.charAt(0).toUpperCase() : "C";
  const avatarUrl =
    post.author?.avatar && (post.author.avatar["96"] || post.author.avatar["48"] || post.author.avatar["24"]);

  return (
    <article className="max-w-4xl mx-auto">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6 font-mono">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="text-xs uppercase tracking-widest text-primary px-3 py-1 rounded-md bg-accent border border-border"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-[1.1] tracking-tight mb-8">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-muted-foreground mb-10 font-mono text-sm">
        {post.author?.name && (
          <div className="flex items-center gap-2">
            <User className="size-4" />
            <span>{post.author.name}</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <span>{formattedDate}</span>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover aspect-[16/9]"
            loading="lazy"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none font-default text-foreground/90 text-lg leading-relaxed [&_p]:mb-6 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-8 [&_img]:rounded-lg [&_img]:my-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground mr-2">Tags:</span>
          {post.tags.map((tag, i) => (
            <span
              data-index={i}
              key={tag.id}
              className="text-xs px-3 py-1 rounded-md bg-muted text-muted-foreground border border-border font-mono"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-16" />

      {}
      {post.author?.name && (
        <Card className="bg-card border border-border shadow-md">
          <CardContent className="px-8 py-2">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <Avatar className="size-20 shrink-0 rounded-lg border border-border">
                {avatarUrl && <AvatarImage src={avatarUrl} alt={post.author.name} />}
                <AvatarFallback className="bg-primary text-primary-foreground font-default text-2xl font-semibold rounded-lg">
                  {authorInitial}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Author
                </p>
                <h3 className="font-default text-2xl font-semibold text-foreground mb-3">
                  {post.author.name}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  A lead mentor at Code Hub, dedicated to fostering open-source collaboration and technical excellence within our community.
                </p>
                {post.author.link && (
                  <Link
                    to={post.author.link}
                    className="inline-flex items-center gap-2 mt-4 text-primary font-mono text-sm hover:underline"
                  >
                    View all updates
                    <ArrowRight className="size-4" />
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="h-48 rounded-lg bg-muted/40 animate-pulse" />
            <div className="h-6 w-3/4 rounded bg-muted/40 animate-pulse" />
            <div className="h-4 w-full rounded bg-muted/40 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-muted-foreground font-mono text-sm">
        No related updates available.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => {
        const date = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Link
            data-index={i}
            key={post.id}
            to={post.link || "#"}
            className="group block"
          >
            <Card className="bg-card border border-border h-full overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
              {post.featuredImage && (
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              )}
              <CardContent className="px-6">
                {date && (
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                    {date}
                  </p>
                )}
                <h3 className="font-default text-xl font-semibold text-foreground leading-tight mb-3 group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <div
                    className="text-muted-foreground text-sm leading-relaxed line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                )}
                <div className="mt-4 inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest">
                  Read more
                  <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  const singlePostQuery = new WP_Query(currentWPQueryParams);

  const relatedPostsQuery = new WP_Query({
    post_type: currentWPQueryParams?.post_type || "post",
    per_page: 3,
    orderby: "date",
    order: "DESC",
  });

  return (
    <section data-section-id="4507"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-6 md:px-10 lg:px-16"
    >
      <div className="max-w-7xl mx-auto">
        {}
        <div className="max-w-4xl mx-auto mb-12">
          <Link
            to="/CommunityPage"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back to Community
          </Link>
        </div>

        {}
        <PostsProvider wp_query={singlePostQuery}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-24 md:mt-32">
          <div className="max-w-4xl mx-auto mb-12">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
              Stay updated
            </p>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground leading-tight tracking-tight">
              Recent Club News
            </h2>
          </div>

          <PostsProvider wp_query={relatedPostsQuery}>
            <RelatedPostsContent />
          </PostsProvider>

          <div className="mt-12 flex justify-center">
            <Link to="/CommunityPage">
              <Button
                variant="outline"
                size="lg"
                className="border-border bg-background hover:bg-accent font-mono text-xs uppercase tracking-widest"
              >
                View All Updates
                <ArrowRight className="size-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}